import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { getTodayCO } from '@/lib/dateUtils';

export interface AppointmentRow {
  id: string;
  client_name: string;
  client_email: string | null;
  appointment_date: string;
  start_time: string;
  status: string;
  payment_status: string;
  created_at: string;
}

export interface DashboardStats {
  total: number;
  today: number;
  pendingPayments: number;
}

export interface TopCustomer {
  id: string;
  name: string;
  email: string;
  total_appointments: number;
  total_spent: number;
}

export interface DormantCustomer {
  id: string;
  name: string;
  email: string;
  last_visit: string;
  days_since: number;
  total_appointments: number;
}

interface UseDashboardDataReturn {
  stats: DashboardStats;
  appointments: AppointmentRow[];
  topCustomers: TopCustomer[];
  dormantCustomers: DormantCustomer[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}



export function useDashboardData(): UseDashboardDataReturn {
  const [stats, setStats] = useState<DashboardStats>({ total: 0, today: 0, pendingPayments: 0 });
  const [appointments, setAppointments] = useState<AppointmentRow[]>([]);
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([]);
  const [dormantCustomers, setDormantCustomers] = useState<DormantCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = () => setTick((t) => t + 1);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const today = getTodayCO();

        // Fetch appointments
        const { data, error: fetchError } = await supabase
          .from('appointments')
          .select('id, client_name, client_email, appointment_date, start_time, status, payment_status, created_at')
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        if (cancelled) return;

        const rows: AppointmentRow[] = data ?? [];
        const total = rows.length;
        const todayCount = rows.filter((r) => r.appointment_date === today).length;
        const pendingPayments = rows.filter((r) => r.payment_status === 'pending').length;

        setStats({ total, today: todayCount, pendingPayments });
        setAppointments(rows);

        // Fetch customers with stats for widgets
        const { data: custData } = await supabase
          .from('customers')
          .select('id, name, email');

        if (cancelled) return;

        const customers = custData ?? [];
        if (customers.length === 0) {
          setTopCustomers([]);
          setDormantCustomers([]);
          return;
        }

        const customerIds = customers.map((c) => c.id as string);

        // Fetch appointments per customer
        const { data: apptData } = await supabase
          .from('appointments')
          .select('id, customer_id, appointment_date, status, payment_status')
          .in('customer_id', customerIds);

        const custAppts = apptData ?? [];
        const apptIds = custAppts.map((a) => a.id as string);

        // Fetch paid amounts
        let paymentsMap = new Map<string, number>();
        if (apptIds.length > 0) {
          const { data: payData } = await supabase
            .from('payments')
            .select('appointment_id, amount, status')
            .in('appointment_id', apptIds)
            .eq('status', 'paid');

          for (const p of payData ?? []) {
            const prev = paymentsMap.get(p.appointment_id as string) ?? 0;
            paymentsMap.set(p.appointment_id as string, prev + (p.amount as number));
          }
        }

        // Build stats per customer
        const statsMap = new Map<string, { total: number; spent: number; lastVisit: string | null }>();
        for (const appt of custAppts) {
          const cid = appt.customer_id as string;
          const prev = statsMap.get(cid) ?? { total: 0, spent: 0, lastVisit: null };
          const paid = paymentsMap.get(appt.id as string) ?? 0;
          const date = appt.appointment_date as string;
          statsMap.set(cid, {
            total: prev.total + 1,
            spent: prev.spent + paid,
            lastVisit: !prev.lastVisit || date > prev.lastVisit ? date : prev.lastVisit,
          });
        }

        // Top customers by total spent
        const enriched = customers
          .map((c) => {
            const s = statsMap.get(c.id as string) ?? { total: 0, spent: 0, lastVisit: null };
            return {
              id: c.id as string,
              name: c.name as string,
              email: c.email as string,
              total_appointments: s.total,
              total_spent: s.spent,
              last_visit: s.lastVisit,
            };
          })
          .filter((c) => c.total_appointments > 0);

        const top = [...enriched]
          .sort((a, b) => b.total_spent - a.total_spent)
          .slice(0, 5)
          .map(({ id, name, email, total_appointments, total_spent }) => ({
            id, name, email, total_appointments, total_spent,
          }));

        // Dormant customers — with at least 1 visit, sorted by oldest last_visit
        const todayDate = new Date(today);
        const dormant = [...enriched]
          .filter((c) => c.last_visit !== null)
          .map((c) => {
            const lastDate = new Date(c.last_visit as string);
            const diffMs = todayDate.getTime() - lastDate.getTime();
            const days_since = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            return {
              id: c.id,
              name: c.name,
              email: c.email,
              last_visit: c.last_visit as string,
              days_since,
              total_appointments: c.total_appointments,
            };
          })
          .sort((a, b) => b.days_since - a.days_since)
          .slice(0, 5);

        if (!cancelled) {
          setTopCustomers(top);
          setDormantCustomers(dormant);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[Dashboard] Error fetching data:', err);
        setError('No se pudo cargar la información. Intenta de nuevo.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { stats, appointments, topCustomers, dormantCustomers, loading, error, refetch };
}
